/* [PATCH opal-onboarding-E v1] Dialog v čarovniku »Nova baza« (korak 2 → Opal → Naprej):
   pokaže Opalovo bazo (naprava · baza · davčna), ki jo je agent našel po davčni
   podjetja, in po potrditvi ustvari ukaz za poln prevzem (agent → Celery → uvoz).
   Endpointi (api/opal_sync.py, blok [PATCH opal-onboarding v1]):
     GET  /opal-sync/onboarding/predlog/{company_id}
     POST /opal-sync/onboarding/{company_id}   {opal_baza, naprava_id}
   Načrt: NACRT_opal_onboarding_en_klik_v3_20260909.md §2.1
*/
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader, Server, X } from 'lucide-react';
import { api } from '../../lib/api';

type Kandidat = {
  opal_baza: string;
  naziv_opal: string | null;
  status: string;
  naprava_id: number;
  naprava: string;
  company_id: string | null;
  opal_sync_pisi: boolean;
};

type Predlog = {
  podjetje: string;
  davcna: string | null;
  kandidati: Kandidat[];
  stevci: { partnerji: number; konti: number };
  prazno: boolean;
  ziv_ukaz: any | null;
  zadnji_ukaz: any | null;
};

type Props = {
  companyId: string;
  companyName: string;
  onZapri: () => void;
  onRocniUvoz: () => void;              // → korak 3 (xlsx), kot doslej
  onUkazUstvarjen: (ukaz: any) => void;
};

function napakaBesedilo(e: any): string {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg || JSON.stringify(x)).join('; ');
  return e?.message || 'Neznana napaka';
}

export const OpalOnboardingDialog: React.FC<Props> = ({ companyId, companyName, onZapri, onRocniUvoz, onUkazUstvarjen }) => {
  const [predlog, setPredlog] = useState<Predlog | null>(null);
  const [nalagam, setNalagam] = useState(true);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [izbrana, setIzbrana] = useState<string>('');      // `${naprava_id}|${opal_baza}`
  const [izvajam, setIzvajam] = useState(false);

  const nalozi = () => {
    setNalagam(true);
    setNapaka(null);
    api.get('/opal-sync/onboarding/predlog/' + companyId)
      .then((r: any) => {
        const p: Predlog = r.data;
        setPredlog(p);
        if (p.kandidati.length >= 1 && !izbrana) {
          const k = p.kandidati[0];
          setIzbrana(`${k.naprava_id}|${k.opal_baza}`);
        }
      })
      .catch((e: any) => setNapaka(napakaBesedilo(e)))
      .finally(() => setNalagam(false));
  };

  useEffect(() => { nalozi(); }, [companyId]);   // [PATCH opal-onboarding-E v1b] brez eslint komentarja (pravila v projektu ni)

  const kandidat = predlog?.kandidati.find((k) => `${k.naprava_id}|${k.opal_baza}` === izbrana) || null;
  const zasedena = !!kandidat && kandidat.status === 'potrjen' && !!kandidat.company_id && kandidat.company_id !== companyId;
  const smeUvoziti = !!predlog && !!kandidat && predlog.prazno && !predlog.ziv_ukaz && !zasedena && !izvajam;

  const uvozi = async () => {
    if (!kandidat) return;
    setIzvajam(true);
    setNapaka(null);
    try {
      const r = await api.post('/opal-sync/onboarding/' + companyId, {
        opal_baza: kandidat.opal_baza, naprava_id: kandidat.naprava_id,
      });
      onUkazUstvarjen(r.data?.ukaz || null);
    } catch (e: any) {
      setNapaka(napakaBesedilo(e));
    } finally {
      setIzvajam(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={onZapri}>
      <div
        className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 bg-blue-50 dark:bg-blue-950/40 border-b border-blue-200 dark:border-blue-900">
          <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0">
            <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">Uvoz iz Opala — potrdi bazo</h3>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{companyName} · davčna {predlog?.davcna || '—'}</div>
          </div>
          <button onClick={onZapri} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" title="Zapri">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm">
          {nalagam && (
            <div className="flex items-center gap-2 text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Iščem Opalovo bazo po davčni …</div>
          )}

          {!nalagam && predlog && predlog.kandidati.length === 0 && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" /> V Opalu ni baze z davčno {predlog.davcna || '—'}</div>
              <div className="mt-1 text-xs">Agent osveži seznam baz vsakih 5 minut. Če je baza v Opalu nova, počakaj in klikni Osveži; če se davčna razlikuje, jo preveri v Opalovem registru firm.</div>
            </div>
          )}

          {!nalagam && predlog && predlog.kandidati.length > 0 && (
            <div className="space-y-2">
              <div className="text-gray-700 dark:text-gray-300">
                {predlog.kandidati.length === 1 ? 'V Opalu je najdena baza:' : 'V Opalu je več baz s to davčno — izberi pravo:'}
              </div>
              {predlog.kandidati.map((k) => {
                const key = `${k.naprava_id}|${k.opal_baza}`;
                const tuja = k.status === 'potrjen' && !!k.company_id && k.company_id !== companyId;
                return (
                  <label key={key} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${izbrana === key ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40' : 'border-gray-200 dark:border-gray-700'}`}>
                    <input type="radio" name="opal-baza" className="mt-1" checked={izbrana === key} onChange={() => setIzbrana(key)} />
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white text-base">{k.opal_baza}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{k.naziv_opal || '—'} · {k.naprava}</div>
                      {tuja && <div className="text-xs text-red-600 dark:text-red-400 mt-1">Baza je že vezana na drugo podjetje.</div>}
                    </div>
                  </label>
                );
              })}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Je to prava baza za <span className="font-medium text-gray-700 dark:text-gray-200">{predlog.podjetje}</span>? Po potrditvi agent prenese partnerje, TRR, konte, SM, projekte, vrste knjiženja ter prejete in izdane račune (učni vzorci). Uvoz teče v ozadju (nekaj minut); ob koncu dobiš obvestilo. Sinhronizacija se nato vklopi samodejno.
              </div>
            </div>
          )}

          {!nalagam && predlog && !predlog.prazno && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 text-xs">
              Podjetje ni prazno ({predlog.stevci.partnerji} partnerjev, {predlog.stevci.konti} kontov) — poln prevzem je dovoljen samo za novo podjetje. Uporabi ročni uvoz.
            </div>
          )}
          {!nalagam && predlog?.ziv_ukaz && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 text-xs">
              Za to podjetje že teče ukaz #{predlog.ziv_ukaz.id} ({predlog.ziv_ukaz.status}) iz baze {predlog.ziv_ukaz.opal_baza}.
            </div>
          )}
          {napaka && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs">{napaka}</div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex gap-2">
            <button onClick={nalozi} disabled={nalagam || izvajam} className="px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">Osveži</button>
            <button onClick={onRocniUvoz} disabled={izvajam} className="px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">Ročni uvoz (xlsx)</button>
          </div>
          <button
            onClick={uvozi}
            disabled={!smeUvoziti}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            title={!predlog?.prazno ? 'Podjetje ni prazno' : predlog?.ziv_ukaz ? 'Ukaz že teče' : zasedena ? 'Baza je vezana na drugo podjetje' : ''}
          >
            {izvajam ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Da, uvozi iz {kandidat?.opal_baza || 'Opala'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpalOnboardingDialog;
